import React, {useCallback, useEffect, useRef, useState} from "react";
import {Button, Descriptions, Modal, Select, Space, Tag} from "antd";
import {Building2} from "lucide-react";
import {useSelector} from "react-redux";
import {RootState} from "../../redux/store";
import {AdminLayout} from "../../components/layout/AdminLayout";
import {GenericTable} from "../../components/table/GenericTable";
import {adminRequest, buildQuery, getReturnedObject} from "../../api/adminApi";
import {message} from "../../utils/antdFeedback";
import {HeroHeader, Card, SearchField, COLORS} from "../../designSystem";

export default function CorporateAccounts() {
  const {token} = useSelector((state: RootState) => state.account);
  const [items, setItems] = useState<any[]>([]);
  const [searchParam, setSearchParam] = useState("");
  const [status, setStatus] = useState("inactive");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<any>(null);
  const [activating, setActivating] = useState(false);
  const busy = useRef(false);
  const sequence = useRef(0);
  const [pageInfo, setPageInfo] = useState({pageNumber: 1, pageSize: 10, totalItems: 0, totalPages: 0});
  const load = useCallback(async (pageNumber = 1) => {
    if (!token) return;
    const request = ++sequence.current;
    setLoading(true);
    try {
      const data = getReturnedObject(await adminRequest(`/corporate/accounts${buildQuery({searchParam,
        isActive: status === "all" ? undefined : status === "active", pageNumber, pageSize: 10})}`, {token}));
      if (request !== sequence.current) return;
      setItems((data?.items || []).map((row: any) => ({...row, key: row.id})));
      setPageInfo({pageNumber: data.pageNumber, pageSize: data.pageSize, totalItems: data.totalRecords,
        totalPages: Math.ceil(data.totalRecords / data.pageSize)});
    } catch (error: any) {
      if (request === sequence.current) message.error(error.message || "Unable to load corporate accounts");
    } finally {
      if (request === sequence.current) setLoading(false);
    }
  }, [token, searchParam, status]);
  const invalidateRequests = useCallback(() => {sequence.current++;}, []);
  useEffect(() => {load(1); return invalidateRequests;}, [load, invalidateRequests]);

  const activate = async () => {
    if (!token || !selected || busy.current) return;
    busy.current = true;
    setActivating(true);
    try {
      const result = await adminRequest<any>(`/corporate/accounts/${selected.id}/activate`, {token, method: "POST"});
      message.success(result.message || "Corporate account activated");
      setSelected(null);
      await load(1);
    } catch (error: any) {
      message.error(error.message || "Could not activate corporate account");
    } finally {busy.current = false; setActivating(false);}
  };

  return <AdminLayout>
    <main className="h-full overflow-y-auto px-6 py-8 md:px-8" style={{background: COLORS.surface}}>
      <HeroHeader icon={Building2} title="Corporate Accounts" subtitle="Review corporate accounts and activate them to enable payments and withdrawals."/>
      <Card className="mt-8 p-5">
        <Space className="flex flex-wrap" size={12}>
          <SearchField placeholder="Search account, reference, bank account or admin email" value={searchParam}
            onChange={event => setSearchParam(event.target.value)} className="min-w-[300px]"/>
          <Select value={status} onChange={setStatus} style={{width: 180}} options={[
            {value: "inactive", label: "Inactive accounts"}, {value: "active", label: "Active accounts"}, {value: "all", label: "All accounts"},
          ]}/>
          <Button onClick={() => load(pageInfo.pageNumber)} loading={loading}>Refresh</Button>
        </Space>
        <GenericTable data={items} loading={loading} pageInfo={pageInfo} handlePageChange={load} columns={[
          {title: "Account", dataIndex: "name", key: "name"},
          {title: "Reference", dataIndex: "paymentReference", key: "paymentReference"},
          {title: "Account admins", key: "admins", render: (_: any, row: any) => row.adminEmails?.join(", ") || "—"},
          {title: "Bank", dataIndex: "bank", key: "bank"},
          {title: "Account holder", dataIndex: "accountName", key: "accountName"},
          {title: "Account number", dataIndex: "accountNumber", key: "accountNumber"},
          {title: "Members", dataIndex: "memberCount", key: "memberCount"},
          {title: "Status", key: "status", render: (_: any, row: any) => <Tag color={row.isActive ? "green" : "red"}>{row.isActive ? "Active" : "Inactive"}</Tag>},
          {title: "Created", dataIndex: "dateCreated", key: "dateCreated", render: (value: string) => new Date(value).toLocaleString()},
          {title: "Action", key: "action", render: (_: any, row: any) => row.isActive ? "Activated" : <Button type="primary" onClick={() => setSelected(row)}>Activate</Button>},
        ]}/>
      </Card>
      <Modal title="Activate corporate account" open={!!selected} onOk={activate} okText="Activate account"
        confirmLoading={activating} onCancel={() => !activating && setSelected(null)} cancelButtonProps={{disabled: activating}} closable={!activating}>
        <p className="mb-4">Activation enables this account to accept payments and request withdrawals.</p>
        <Descriptions bordered size="small" column={1}>
          <Descriptions.Item label="Account">{selected?.name}</Descriptions.Item>
          <Descriptions.Item label="Reference">{selected?.paymentReference}</Descriptions.Item>
          <Descriptions.Item label="Description">{selected?.description || "—"}</Descriptions.Item>
          <Descriptions.Item label="Bank">{selected?.bank}</Descriptions.Item>
          <Descriptions.Item label="Account holder">{selected?.accountName}</Descriptions.Item>
          <Descriptions.Item label="Account number">{selected?.accountNumber}</Descriptions.Item>
        </Descriptions>
      </Modal>
    </main>
  </AdminLayout>;
}
